import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface SoftphoneContextValue {
  /** Whether the idle dialer panel is open (call/incoming states show regardless). */
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

// Non-throwing default so the header trigger and the panel degrade to no-ops if ever rendered
// outside the provider (tests, storybook). In the app both live under <SoftphoneProvider/>.
const SoftphoneContext = createContext<SoftphoneContextValue>({
  isOpen: false,
  open: () => {},
  close: () => {},
  toggle: () => {},
});

/**
 * Shares the softphone's open-state between the header launcher button and the (single)
 * Softphone panel mounted at the app root — so the dialer opens from the header instead of a
 * floating button (#2817). Wrap both the routed Layouts and <SoftphoneContainer/> with this.
 */
export function SoftphoneProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((o) => !o), []);
  const value = useMemo(() => ({ isOpen, open, close, toggle }), [isOpen, open, close, toggle]);
  return <SoftphoneContext.Provider value={value}>{children}</SoftphoneContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSoftphone() {
  return useContext(SoftphoneContext);
}
